import { AfterViewInit, Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import { ProductModel } from '../product-model';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Entity } from '../../entity/entity';
import { EntityLine } from '../../entity/entity-line';
import { EntityLineHeader } from '../../entity/entity-line-header';
import { ConfigurationSaveInformation } from '../../model/configuration-save-information';
import { GuruEntity } from '../../model/guru-entity';
import { IecLeadTime } from '../../model/iec-lead-time';
import { SfrEntry } from '../../model/sfr-entry';
import { Sku } from '../../model/sku';
import { TagInformation } from '../../model/tag-information';
import { UnitRatingResponse } from '../../model/unit-rating-response';
import { EntityLineService } from '../../services/entity-line-service';
import { ModelService } from '../../services/model-service';
import { EntityKeyUtils } from '../../utils/entity-key-utils';
import { StringUtils } from '../../utils/string-utils';
import { ConfigurationService } from '../configuration.service';
import { SelectionUtils } from '../selection-utils';
import { UnitConversionService } from '../unit-conversion.service';
import { SkusAndPricesComponent } from '../common/skus-and-prices/skus-and-prices.component';
import { UnitInformationComponent } from '../common/unit-information/unit-information.component';
import { AirHandlerUnitSystemInputsComponent } from './air-handler-unit-system-inputs/air-handler-unit-system-inputs.component';
import { AirHandlerUnitMechanicalConnectionsComponent } from './air-handler-unit-mechanical-connections/air-handler-unit-mechanical-connections.component';
import { AirHandlerUnitReturnAirModuleComponent } from './air-handler-unit-return-air-module/air-handler-unit-return-air-module.component';
import { AirHandlerUnitAccessModuleComponent } from './air-handler-unit-access-module/air-handler-unit-access-module.component';
import { AirHandlerUnitControlsComponent } from './air-handler-unit-controls/air-handler-unit-controls.component';
import { AirHandlerUnitCoilModuleComponent } from './air-handler-unit-coil-module/air-handler-unit-coil-module.component';
import { AirHandlerUnitStandardFeaturesComponent } from './air-handler-unit-standard-features/air-handler-unit-standard-features.component';
import { AirHandlerUnitPerformanceInputComponent } from './air-handler-unit-performance-input/air-handler-unit-performance-input.component';
import { AirHandlerUnitPerformanceOutputComponent } from './air-handler-unit-performance-output/air-handler-unit-performance-output.component';
import { AirHandlerUnitFanCurveChartComponent } from './air-handler-unit-fan-curve-chart/air-handler-unit-fan-curve-chart.component';
import { AirHandlerUnitFanPerformanceSummaryComponent } from './air-handler-unit-fan-performance-summary/air-handler-unit-fan-performance-summary.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AhyOperatingPoint } from '../../model/ahy-operating-point';
import { FanCurvePerformanceEntry } from '../../model/ratings/fan-curve-performance-entry';

@Component({
  selector: 'app-air-handler-unit-product-model',
  imports: [
    UnitInformationComponent,
    AirHandlerUnitSystemInputsComponent,
    AirHandlerUnitMechanicalConnectionsComponent,
    AirHandlerUnitReturnAirModuleComponent,
    AirHandlerUnitAccessModuleComponent,
    AirHandlerUnitControlsComponent,
    AirHandlerUnitCoilModuleComponent,
    AirHandlerUnitStandardFeaturesComponent,
    SkusAndPricesComponent,
    AirHandlerUnitPerformanceInputComponent,
    AirHandlerUnitPerformanceOutputComponent,
    AirHandlerUnitFanCurveChartComponent,
    AirHandlerUnitFanPerformanceSummaryComponent,
    MatProgressSpinnerModule,
  ],
  templateUrl: './air-handler-unit-product-model.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './air-handler-unit-product-model.component.css',
})
export class AirHandlerUnitProductModelComponent implements AfterViewInit, ProductModel {
  private readonly configurationService = inject(ConfigurationService);
  private readonly modelService = inject(ModelService);
  private readonly unitConversionService = inject(UnitConversionService);
  private readonly entityLineService = inject(EntityLineService);
  entity!: Entity;
  readonly entityLine = signal<EntityLine>(undefined!);
  entityLineHeader!: EntityLineHeader;
  readonly taggingDetails = signal<TagInformation[]>(undefined!);
  readonly isBusy = signal(false);
  readonly isLoaded = signal(false);
  readonly errorMessage = signal<string>(undefined!);
  private readonly engineKey = signal<string>(undefined!);
  private modelName = 'AirHandlerUnit';
  private imagePrefix = 'assets/configurator/AirHandlerUnit/';
  readonly unitImagePath = signal<string>(undefined!);
  readonly invalidEntities = signal<string[]>(undefined!);
  readonly selectionEntities = signal<GuruEntity[]>(undefined!);
  readonly unitRatingResponse = signal<UnitRatingResponse>(undefined!);
  readonly operatingPoint = signal<AhyOperatingPoint>(undefined!);
  readonly messages = signal<string[]>(undefined!);
  readonly skus = signal<Sku[]>(undefined!);
  readonly sfrEntries = signal<SfrEntry[]>(undefined!);
  entityLeadTime!: IecLeadTime;
  readonly configurationLeadTime = signal<IecLeadTime>(undefined!);
  slowestLeadTime!: IecLeadTime;

  ngAfterViewInit(): void {
    this.isLoaded.set(true);
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.updateUnitRating();
  }

  initialize(): Observable<string> {
    const subject = new Subject<string>();
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.loadConfiguration(this.modelName, this.entityLineHeader, this.entity.leadTimeCode).subscribe({
      next: (response) => {
        this.engineKey.set(response.engineKey);
        this.entityLine.set(response.line);
        this.taggingDetails.set(response.taggingDetails);
        this.sfrEntries.set(response.sfrEntries);
        this.modelService.getCharacteristicValue(this.engineKey(), 'USUnit', 'FileName').subscribe({
          next: (result) => {
            this.unitImagePath.set(this.imagePrefix + result);
            this.modelService.getInvalidEntities(this.engineKey()).subscribe({
              next: (invalidEntities) => {
                this.invalidEntities.set(invalidEntities);
                this.modelService.getEntitiesStartingWithName(this.engineKey(), 'US').subscribe({
                  next: (entities) => {
                    this.selectionEntities.set(entities);
                    subject.next('Initialization Complete');
                  },
                  error: (error) => this.handleSubjectError(subject, error),
                });
              },
              error: (error) => this.handleSubjectError(subject, error),
            });
          },
          error: (error) => this.handleSubjectError(subject, error),
        });
      },
      error: (error) => this.handleSubjectError(subject, error),
    });
    return subject.asObservable();
  }

  private handleSubjectError(subject: Subject<string>, error: any): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
    this.configurationService.isBusy.set(false);
    subject.error(error);
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
    this.configurationService.isBusy.set(false);
  }

  getEngineKey(): string {
    return this.engineKey();
  }

  quantityChanged(): void {
    this.configurationService.changeLine(this.entityLine().lineId);
  }

  updateTaggingDetails(details: TagInformation[]): void {
    this.errorMessage.set(undefined!);
    this.entityLine().quantityExtended = details.length;
    this.entityLine().quantity = this.entityLine().quantityExtended + this.entityLine().quantityCancelled;
    this.taggingDetails.set(details);
    this.configurationService.changeLine(this.entityLine().lineId);
  }

  validateUnit(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.validateUnit(this.engineKey()).subscribe({
      next: (response) => {
        this.invalidEntities.set(response.invalidEntities);
        this.selectionEntities.set(response.userSelections);
        this.configurationService.changeLine(this.entityLine().lineId);
        this.loadMessagesAndSkus();
      },
      error: (error) => this.handleError(error),
    });
  }

  validateAndRateUnit(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.validateUnit(this.engineKey()).subscribe({
      next: (response) => {
        this.invalidEntities.set(response.invalidEntities);
        this.selectionEntities.set(response.userSelections);
        this.configurationService.changeLine(this.entityLine().lineId);
        this.updateUnitRating();
      },
      error: (error) => this.handleError(error),
    });
  }

  unitTypeChanged(): void {
    this.errorMessage.set(undefined!);
    this.configurationService.changeUnitType(this.engineKey()).subscribe({
      next: (response) => {
        this.invalidEntities.set(response.invalidEntities);
        this.selectionEntities.set(response.selectionEntities);
        this.unitImagePath.set(this.imagePrefix + response.imageFileName);
        this.configurationService.changeLine(this.entityLine().lineId);
        this.updateUnitRating();
      },
      error: (error) => this.handleError(error),
    });
  }

  systemTypeChanged(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.changeSystemType(this.engineKey()).subscribe({
      next: (response) => {
        this.invalidEntities.set(response.invalidEntities);
        this.selectionEntities.set(response.selectionEntities);
        this.unitImagePath.set(this.imagePrefix + response.imageFileName);
        this.configurationService.changeLine(this.entityLine().lineId);
        this.updateUnitRating();
      },
      error: (error) => this.handleError(error),
    });
  }

  private updateUnitRating(): void {
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.getRatingResponse(this.engineKey(), this.selectionEntities()).subscribe({
      next: (response) => {
        this.unitRatingResponse.set(response);
        this.operatingPoint.set(this.calculateOperatingPoint());
        this.loadMessagesAndSkus();
      },
      error: (error) => {
        const errorResponse = new UnitRatingResponse();
        errorResponse.ErrorMessage = error.error;
        this.unitRatingResponse.set(errorResponse);
        this.operatingPoint.set(undefined!);
        this.configurationService.isBusy.set(false);
      },
    });
  }

  private calculateOperatingPoint(): AhyOperatingPoint {
    if ((this.unitRatingResponse()?.PerformanceSummary?.length || 0) > 0) {
      let closest: FanCurvePerformanceEntry = undefined!;
      const userAirflow: number = +(SelectionUtils.getEntity(this.selectionEntities(), 'USFAirflow')?.Value || '0');
      for (const curveData of this.unitRatingResponse().PerformanceSummary) {
        if (!closest) {
          closest = curveData;
        } else {
          const currentAirflowDiff: number = Math.abs(userAirflow - closest.ActualAirflow);
          const curveAirflowDiff: number = Math.abs(userAirflow - curveData.ActualAirflow);
          if (curveAirflowDiff < currentAirflowDiff) {
            closest = curveData;
          }
        }
      }
      if (closest) {
        const operatingPoint = new AhyOperatingPoint();
        operatingPoint.airflow = closest.Airflow;
        operatingPoint.staticPressure = closest.TSP;
        return operatingPoint;
      }
    }
    return undefined!;
  }

  private loadMessagesAndSkus(): void {
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.getMessages(this.engineKey()).subscribe({
      next: (messages) => {
        this.messages.set(undefined!);
        if (messages && messages.length > 0) {
          this.messages.set(messages.filter((message) => message && message.trim().length > 0));
        }
        this.modelService.getConfigurationData(this.engineKey(), this.entity.priceBasisDate, this.entity.priceListId).subscribe({
          next: (data) => {
            this.configurationLeadTime.set(this.determineBestLeadTime(data.bestLeadTime));
            this.skus.set(data.skus);
            this.configurationService.isBusy.set(false);
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      },
      error: (error) => this.handleError(error),
    });
  }

  private determineBestLeadTime(asConfigured: IecLeadTime): IecLeadTime {
    return this.sfrsAreApplied() ? this.slowestLeadTime : asConfigured;
  }

  private sfrsAreApplied(): boolean {
    return this.sfrEntries() && this.sfrEntries().length > 0 && this.sfrEntries().filter((entry) => !entry.ignore).length > 0;
  }

  inputUnitsChanged(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.unitConversionService.convertMeasurementUnits(this.engineKey(), this.selectionEntities()).subscribe({
      next: (selections) => {
        this.selectionEntities.set(selections);
        this.configurationService.changeLine(this.entityLine().lineId);
        this.updateUnitRating();
      },
      error: (error) => this.handleError(error),
    });
  }

  getImagePrefix(): string {
    return this.imagePrefix;
  }

  isOem(): boolean {
    return SelectionUtils.isOem(this.entity.prnField1);
  }

  getSaveInformation(): ConfigurationSaveInformation {
    const information = new ConfigurationSaveInformation();
    information.engineKey = this.engineKey();
    information.entityKey = EntityKeyUtils.convertToFullEntityKey(this.entity.id);
    information.lineId = this.entityLine().lineId;
    information.ratingResponse = this.unitRatingResponse();
    information.sfrEntries = this.sfrEntries();
    information.taggingInformation = this.taggingDetails();
    information.customerItemId = this.entityLine().customerItemId;
    information.poAmount = this.entityLine().numericUserField3;
    information.prnField1 = this.entity.prnField1;
    information.initialUnitPrice = SelectionUtils.getConfigurationPrice(this.skus(), this.sfrEntries());
    return information;
  }

  saveUnit(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.saveConfiguration(this.getSaveInformation()).subscribe({
      next: () => {
        this.entityLineService.getFullEntityLine(this.entityLineHeader).subscribe((line) => {
          this.entityLine.set(line);
          this.entityLineHeader.configurationComplete = line.configurationComplete;
          this.entityLineHeader.quantity = line.quantityExtended;
          this.entityLineHeader.tags = line.externalNotes;
          this.entityLineHeader.description = line.description;
          this.isBusy.set(false);
          this.configurationService.isBusy.set(false);
        });
      },
      error: (error) => this.handleError(error),
    });
  }

  applyMassUpdate(entities: GuruEntity[]): void {
    this.configurationService.isBusy.set(true);
    this.configurationService.applyMassUpdate(this.engineKey(), entities).subscribe({
      next: () => {
        this.validateAndRateUnit();
        this.configurationService.isBusy.set(false);
      },
      error: (error) => this.handleError(error),
    });
  }

  sfrFlagChanged(): void {
    this.configurationService.changeLine(this.entityLine().lineId);
  }
}
